import type { BuildRecipe, GlobalLivery } from "../core/index.ts";
import { defaultLivery, spikeCatalog } from "./spike-catalog.ts";

export interface DemoMech {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly referenceLabel: string;
  readonly sourceUrl: string;
  readonly thumbnailRef: string;
  readonly recipe: BuildRecipe;
}

const assemblyReferenceUrl =
  "https://assetstorev1-prd-cdn.unity3d.com/key-image/b12a461d-aa00-4fd5-8190-f2d272501c3a.jpg";

const fieldLivery: GlobalLivery = {
  paletteId: "field",
  primary: "#747d55",
  accent: "#dfbd63",
  emission: 0.08,
};

const graphiteLivery: GlobalLivery = {
  paletteId: "graphite",
  primary: "#59615e",
  accent: "#c6b681",
  emission: 0.06,
};

export const demoMechs = [
  {
    id: "atlas-cobra",
    label: "Атлас-Кобра",
    description: "Тяжёлый асимметричный паук с крупной плечевой рамой и длинной пушкой.",
    referenceLabel: "Верхний ряд, крайний слева",
    sourceUrl: assemblyReferenceUrl,
    thumbnailRef: "/assets/gallery/atlas-cobra.jpg",
    recipe: {
      schema: 1,
      catalogFingerprint: spikeCatalog.fingerprint,
      base: {
        positionId: "base-main",
        moduleId: "base.spider.heavy",
      },
      placements: [
        {
          positionId: "shoulder-main",
          parentPositionId: "base-main",
          attachmentPointId: "top",
          moduleId: "shoulder.cobra",
        },
        {
          positionId: "cockpit-main",
          parentPositionId: "shoulder-main",
          attachmentPointId: "cockpit",
          moduleId: "cockpit.gunner",
        },
        {
          positionId: "weapon-left-main",
          parentPositionId: "shoulder-main",
          attachmentPointId: "weapon-left",
          moduleId: "weapon.sniper-l1",
        },
        {
          positionId: "weapon-right-main",
          parentPositionId: "shoulder-main",
          attachmentPointId: "weapon-right",
          moduleId: "weapon.double-gun-l1",
        },
        {
          positionId: "backpack-main",
          parentPositionId: "cockpit-main",
          attachmentPointId: "backpack",
          moduleId: "backpack.frame",
        },
        {
          positionId: "antenna-main",
          parentPositionId: "backpack-main",
          attachmentPointId: "antenna",
          moduleId: "antenna.short",
        },
      ],
      unresolved: [],
      livery: fieldLivery,
    },
  },
  {
    id: "emerald-hornet",
    label: "Изумрудный Шершень",
    description: "Лёгкий паук с одной вынесенной энергетической пушкой и высокой антенной.",
    referenceLabel: "Верхний ряд, второй слева",
    sourceUrl: assemblyReferenceUrl,
    thumbnailRef: "/assets/gallery/emerald-hornet.jpg",
    recipe: {
      schema: 1,
      catalogFingerprint: spikeCatalog.fingerprint,
      base: {
        positionId: "base-main",
        moduleId: "base.spider.light",
      },
      placements: [
        {
          positionId: "cockpit-main",
          parentPositionId: "base-main",
          attachmentPointId: "top",
          moduleId: "cockpit.flyer",
        },
        {
          positionId: "weapon-left-main",
          parentPositionId: "cockpit-main",
          attachmentPointId: "weapon-left",
          moduleId: "weapon.shock-rifle-l1",
        },
        {
          positionId: "backpack-main",
          parentPositionId: "cockpit-main",
          attachmentPointId: "backpack",
          moduleId: "backpack.frame",
        },
        {
          positionId: "antenna-main",
          parentPositionId: "backpack-main",
          attachmentPointId: "antenna",
          moduleId: "antenna.long",
        },
      ],
      unresolved: [],
      livery: defaultLivery,
    },
  },
  {
    id: "rail-bastion",
    label: "Рельсовый Бастион",
    description: "Компактный четырёхгусеничный штурмовик с двухствольной пушкой.",
    referenceLabel: "Верхний ряд, крайний справа",
    sourceUrl: assemblyReferenceUrl,
    thumbnailRef: "/assets/gallery/rail-bastion.jpg",
    recipe: {
      schema: 1,
      catalogFingerprint: spikeCatalog.fingerprint,
      base: {
        positionId: "base-main",
        moduleId: "base.tracks.heavy",
      },
      placements: [
        {
          positionId: "cockpit-main",
          parentPositionId: "base-main",
          attachmentPointId: "top",
          moduleId: "cockpit.gunbot",
        },
        {
          positionId: "weapon-left-main",
          parentPositionId: "cockpit-main",
          attachmentPointId: "weapon-left",
          moduleId: "weapon.double-gun-l3",
        },
        {
          positionId: "weapon-right-main",
          parentPositionId: "cockpit-main",
          attachmentPointId: "weapon-right",
          moduleId: "weapon.grenade-l1",
        },
        {
          positionId: "backpack-main",
          parentPositionId: "cockpit-main",
          attachmentPointId: "backpack",
          moduleId: "backpack.tank",
        },
        {
          positionId: "antenna-main",
          parentPositionId: "backpack-main",
          attachmentPointId: "antenna",
          moduleId: "antenna.short",
        },
      ],
      unresolved: [],
      livery: graphiteLivery,
    },
  },
  {
    id: "desert-corsair",
    label: "Пустынный Корсар",
    description: "Низкое колёсное багги с длинной пушкой и парным орудием.",
    referenceLabel: "Средний ряд, второй слева",
    sourceUrl: assemblyReferenceUrl,
    thumbnailRef: "/assets/gallery/desert-corsair.jpg",
    recipe: {
      schema: 1,
      catalogFingerprint: spikeCatalog.fingerprint,
      base: {
        positionId: "base-main",
        moduleId: "base.buggy.light",
      },
      placements: [
        {
          positionId: "cockpit-main",
          parentPositionId: "base-main",
          attachmentPointId: "top",
          moduleId: "cockpit.moto",
        },
        {
          positionId: "weapon-left-main",
          parentPositionId: "cockpit-main",
          attachmentPointId: "weapon-left",
          moduleId: "weapon.sniper-l1",
        },
        {
          positionId: "weapon-right-main",
          parentPositionId: "cockpit-main",
          attachmentPointId: "weapon-right",
          moduleId: "weapon.double-gun-l1",
        },
        {
          positionId: "backpack-main",
          parentPositionId: "cockpit-main",
          attachmentPointId: "backpack",
          moduleId: "backpack.motor",
        },
      ],
      unresolved: [],
      livery: defaultLivery,
    },
  },
  {
    id: "tank-cerberus",
    label: "Танковый Цербер",
    description: "Низкий танк с тремя направлениями огня и компактной антенной.",
    referenceLabel: "Нижний ряд, крайний слева",
    sourceUrl: assemblyReferenceUrl,
    thumbnailRef: "/assets/gallery/tank-cerberus.jpg",
    recipe: {
      schema: 1,
      catalogFingerprint: spikeCatalog.fingerprint,
      base: {
        positionId: "base-main",
        moduleId: "base.tank.light",
      },
      placements: [
        {
          positionId: "cockpit-main",
          parentPositionId: "base-main",
          attachmentPointId: "top",
          moduleId: "cockpit.flyer",
        },
        {
          positionId: "weapon-left-main",
          parentPositionId: "cockpit-main",
          attachmentPointId: "weapon-left",
          moduleId: "weapon.double-gun-l1",
        },
        {
          positionId: "weapon-right-main",
          parentPositionId: "cockpit-main",
          attachmentPointId: "weapon-right",
          moduleId: "weapon.double-gun-l1",
        },
        {
          positionId: "weapon-top-main",
          parentPositionId: "cockpit-main",
          attachmentPointId: "weapon-top",
          moduleId: "weapon.sniper-l1",
        },
        {
          positionId: "backpack-main",
          parentPositionId: "cockpit-main",
          attachmentPointId: "backpack",
          moduleId: "backpack.tank",
        },
        {
          positionId: "antenna-main",
          parentPositionId: "backpack-main",
          attachmentPointId: "antenna",
          moduleId: "antenna.short",
        },
      ],
      unresolved: [],
      livery: fieldLivery,
    },
  },
] as const satisfies readonly DemoMech[];

export type DemoMechId = (typeof demoMechs)[number]["id"];
